import React, { useState, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Spinner from '@atlaskit/spinner';
import Button from '@atlaskit/button';
import Form from '@atlaskit/form';
import {
  AlertableSignalFlowInput,
  TeamInputAtlassianOrgStatusEnum,
} from '@atlassiansox/phobos-client-ts';

import {
  EditContext,
  useFetchErrorController,
  useSuccessController,
  SignalFlowValidateContext,
  SignalFlowValidateResultType,
  useLocationController,
} from '@/controllers';
import meta from '@/meta';
import { apiFetch } from '@/services';
import { flexSpace } from '@/styles';

import CapabilityForm from './capability';
import {
  api2Ui,
  CapabilityFormData,
  ProductFormData,
  SloFormData,
  TeamFormData,
  ui2Api,
} from './data-mapper';
import FormErrors, { reduceFieldErrors } from './form-errors';
import Product from './product';
import CapabilitySLOForm from './slo';
import { actionsLayout, formDrawerOffset, formLayout } from './styles';
import Team from './team';
import { helperCopy } from './copy';
import { fetchSignalFlowAlertable, isSignalFlowValidated } from './slo/fields/utils';

type FormData = TeamFormData | CapabilityFormData | ProductFormData | SloFormData;

const Edit: React.FC = () => {
  const [validateError, setValidateError] = useState<SignalFlowValidateResultType>();
  const [isValidating, setIsValidating] = useState<boolean>(false);

  const { type, publicId, onClose } = React.useContext(EditContext);
  const setSuccessMessage = useSuccessController();
  const { isSLOGroupMemberPage } = useLocationController();

  const ENDPOINT = `${
    isSLOGroupMemberPage ? meta.slogroup.endpoints.slo : meta[type].endpoints.resource
  }/${publicId}`;

  const queryClient = useQueryClient();
  const { onError } = useFetchErrorController();
  const { data: rawData, isInitialLoading: isLoadingGet } = useQuery(
    [type, publicId],
    () => apiFetch(ENDPOINT),
    { onError }
  );
  const data = rawData ? api2Ui[type](rawData) : null;

  const mutation = useMutation(
    (payload: FormData) =>
      apiFetch(ENDPOINT, {
        method: 'PUT',
        body: ui2Api[type]({ ...data, ...(payload as any) }) as any,
      }),
    {
      onError,
      onSuccess: (_data, _variables, _context) => {
        queryClient.invalidateQueries({ queryKey: [type] });
        // Also invalidate queries to reflect slo changes in search results
        queryClient.invalidateQueries({ queryKey: ['capability'] });
        queryClient.invalidateQueries({ queryKey: ['search'] });
      },
    }
  );

  React.useEffect(() => {
    if (mutation.isSuccess) {
      setSuccessMessage({
        title: 'Edit Success',
        message: `Nice. We've successfully updated the ${meta[type].copy.default} '${mutation.data.name}'. This change should now be reflected in the UI.`,
      });
      onClose();
    }
  }, [mutation.isSuccess, mutation.data, setSuccessMessage, type, onClose]);

  const isLoading = isLoadingGet || mutation.isLoading;
  const isFormLoading = isLoading || isValidating;

  async function submitHandler<T extends Parameters<typeof mutation.mutate>[0]>(
    payload: T
  ): Promise<object | undefined> {
    // Validate SLO SFx queries before submitting
    if (type === 'slo') {
      const { goodQuery, badQuery, objective } = payload as SloFormData;
      // Only validate when either good or bad query is populated
      if (goodQuery || badQuery) {
        if (validateError) setValidateError(undefined);
        setIsValidating(true);
        try {
          const validatePayload: AlertableSignalFlowInput = {
            goodQuery: goodQuery || '',
            badQuery: badQuery || '',
            objective: Number(objective || 0),
          };
          const err = await fetchSignalFlowAlertable(validatePayload);
          setValidateError(err);

          if (!isSignalFlowValidated(err)) {
            return undefined; // skip submitting
          }
        } catch (e) {
          onError(e);
          return undefined; // skip submitting
        } finally {
          setIsValidating(false);
        }
      }
    }

    // eslint-disable-next-line no-param-reassign
    if (type === 'team' || type === 'product') payload.name = payload.name.trim();

    try {
      await mutation.mutateAsync(payload);
      return undefined;
    } catch (error) {
      if (error.response && error.response.status === 400) {
        return reduceFieldErrors(await error.response.json());
      }
      return undefined;
    }
  }

  const isOrgStatusAssigned = data?.atlassianOrgStatus === TeamInputAtlassianOrgStatusEnum.Assigned;

  return (
    <div data-testid="edit-form" css={formDrawerOffset}>
      <div css={[flexSpace]}>
        <h3>{isSLOGroupMemberPage ? 'SLO Group Member' : `Edit ${meta[type].copy.default}`}</h3>
        {!isSLOGroupMemberPage && (helperCopy[type].discovery as ReactNode)}
      </div>
      <Form<(API.CapabilityPayload | API.SLOPayload | API.BasicPayload) & { publicId: string }>
        onSubmit={submitHandler}
      >
        {({ formProps }) => (
          <form {...formProps} noValidate>
            <div css={formLayout}>
              {/* eslint-disable react/jsx-props-no-spreading */}
              {type === 'capability' && <CapabilityForm isLoading={isLoading} {...data} />}
              {type === 'slo' && (
                <SignalFlowValidateContext.Provider value={{ isValidating, error: validateError }}>
                  <CapabilitySLOForm isLoading={isFormLoading} {...data} />
                </SignalFlowValidateContext.Provider>
              )}
              {type === 'product' && <Product isLoading={isLoading} {...data} />}
              {type === 'team' && <Team isLoading={isLoading} {...data} />}

              {mutation.error && <FormErrors />}
            </div>
            <div css={actionsLayout}>
              <Button onClick={onClose}>Cancel</Button>
              {!isSLOGroupMemberPage && (
                <Button
                  type="submit"
                  isDisabled={isFormLoading}
                  overlay={isFormLoading && <Spinner />}
                  appearance="primary"
                >
                  {isOrgStatusAssigned ? 'Verify Atlassian Org and Save' : 'Confirm'}
                </Button>
              )}
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};

export default Edit;
